import React from "react";
import {
  Autorenew,
  DescriptionOutlined,
  OpenInNew,
  Replay,
  WarningAmber,
} from "@mui/icons-material";
import { Box, Button, Tooltip, Typography } from "@mui/material";

import { MaterialStatusUiStatus } from "../../../../models/general/material-status";
import { translate } from "../../../../translate/translate";

interface MaterialStatusActionProps {
  status: MaterialStatusUiStatus;
  disabled?: boolean;
  errorDetail?: string | null;
  onGenerate: () => void;
  onOpen: () => void;
  onRetry: () => void;
}

const actionSize = {
  height: 36,
  minWidth: 190,
  width: "190px !important",
  flexShrink: 0,
};

const MaterialStatusAction = ({
  status,
  disabled = false,
  errorDetail,
  onGenerate,
  onOpen,
  onRetry,
}: MaterialStatusActionProps) => {
  if (status === "generating") {
    return (
      <Box
        role="progressbar"
        aria-label={translate.t("material_status_generating")}
        sx={{
          ...actionSize,
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          borderRadius: 1,
          backgroundColor: "#C7D4F0",
          cursor: "wait",
          color: "#FFFFFF",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "primary.main",
          }}
        />
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(110deg, transparent 25%, rgba(255,255,255,0.38) 45%, transparent 65%)",
            backgroundSize: "200% 100%",
            animation: "material-status-shimmer 1.4s linear infinite",
            "@keyframes material-status-shimmer": {
              from: { backgroundPosition: "200% 0" },
              to: { backgroundPosition: "-200% 0" },
            },
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            width: "auto !important",
          }}
        >
          <Autorenew
            fontSize="small"
            sx={{
              animation: "material-status-spin 1s linear infinite",
              "@keyframes material-status-spin": {
                to: { transform: "rotate(360deg)" },
              },
            }}
          />
          <Typography component="span" variant="button" sx={{ color: "inherit", fontSize: 12 }}>
            {translate.t("material_status_generating")}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (status === "success") {
    return (
      <Button
        type="button"
        variant="contained"
        color="success"
        startIcon={<OpenInNew />}
        onClick={onOpen}
        sx={{ ...actionSize, whiteSpace: "nowrap" }}
      >
        {translate.t("open_material_status")}
      </Button>
    );
  }

  if (status === "error") {
    return (
      <Box
        role="alert"
        sx={{
          display: "inline-flex",
          height: actionSize.height,
          width: "auto !important",
          flexShrink: 0,
        }}
      >
        <Tooltip title={errorDetail || translate.t("material_status_generation_error")} arrow>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              color: "error.dark",
              backgroundColor: "#FFF1F1",
              border: 1,
              borderRight: 0,
              borderColor: "error.light",
              borderRadius: "8px 0 0 8px",
              whiteSpace: "nowrap",
              width: "auto !important",
            }}
          >
            <WarningAmber fontSize="small" />
            <Typography component="span" variant="button" sx={{ color: "inherit", fontSize: 12 }}>
              {translate.t("material_status_generation_failed")}
            </Typography>
          </Box>
        </Tooltip>
        <Button
          type="button"
          variant="contained"
          color="error"
          startIcon={<Replay />}
          onClick={onRetry}
          sx={{ borderRadius: "0 8px 8px 0", whiteSpace: "nowrap", boxShadow: "none" }}
        >
          {translate.t("material_status_retry")}
        </Button>
      </Box>
    );
  }

  return (
    <Tooltip
      title={disabled ? translate.t("material_status_analysis_unavailable") : ""}
      arrow
    >
      <span style={{ display: "inline-flex" }}>
        <Button
          type="button"
          variant="contained"
          color="primary"
          startIcon={<DescriptionOutlined />}
          onClick={onGenerate}
          disabled={disabled}
          sx={{ ...actionSize, whiteSpace: "nowrap" }}
        >
          {translate.t("generate_material_status")}
        </Button>
      </span>
    </Tooltip>
  );
};

export default MaterialStatusAction;
